import { useEffect, useState } from 'react';
import { FaSlack, FaRocketchat } from 'react-icons/fa';
import { FiZap, FiUsers } from 'react-icons/fi';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getAuth, getIdToken } from 'firebase/auth';

const Notifications = () => {
  const { user } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState(false);
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('');
  const [customWebhookUrl, setCustomWebhookUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null); // To track which setting is being updated
  const [isSavingIntegration, setIsSavingIntegration] = useState<string | null>(null); // To track which integration is saving
  const { currentPlan } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!user) {
        setIsLoading(false); // Set loading to false if no user
        return;
      }
      setIsLoading(true);
      try {
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setEmailNotifications(userData.notifications?.email ?? false);
          setSmsNotifications(userData.notifications?.sms ?? false);
          setWeeklySummary(userData.notifications?.weeklySummary ?? false);
          setMonthlySummary(userData.notifications?.monthlySummary ?? false);
          // Fetch integration URLs
          setSlackWebhookUrl(userData.notifications?.slack_webhook || '');
          setTeamsWebhookUrl(userData.notifications?.teams_webhook || '');
          setCustomWebhookUrl(userData.notifications?.custom_webhook || '');
        } else {
          // Optional: Initialize settings if they don't exist
          // await setDoc(userRef, { notifications: { email: false, sms: false, weeklySummary: false, monthlySummary: false, slack_webhook: '', teams_webhook: '', custom_webhook: '' } }, { merge: true });
          // If initializing, set local state accordingly or refetch
        }
      } catch (err) {
        console.error('Failed to fetch notification settings:', err);
        toast.error('Failed to load notification settings.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotificationSettings();
  }, [user]);

  const handleToggleSetting = async (settingKey: 'email' | 'sms' | 'weeklySummary' | 'monthlySummary', enabled: boolean) => {
    if (!user || isUpdating) return;

    setIsUpdating(settingKey);
    const originalToggleState = { emailNotifications, smsNotifications, weeklySummary, monthlySummary };

    // Optimistically update UI
    if (settingKey === 'email') setEmailNotifications(enabled);
    else if (settingKey === 'sms') setSmsNotifications(enabled);
    else if (settingKey === 'weeklySummary') setWeeklySummary(enabled);
    else if (settingKey === 'monthlySummary') setMonthlySummary(enabled);
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        [`notifications.${settingKey}`]: enabled,
      });
      toast.success(`${settingKey.charAt(0).toUpperCase() + settingKey.slice(1)} notifications ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (error: unknown) {
      console.error(`Failed to update ${settingKey} notifications:`, error);
      toast.error(`Failed to update ${settingKey} notifications: ${(error as Error).message || 'Please try again.'}`);
      // Revert UI on error
      setEmailNotifications(originalToggleState.emailNotifications);
      setSmsNotifications(originalToggleState.smsNotifications);
      setWeeklySummary(originalToggleState.weeklySummary);
      setMonthlySummary(originalToggleState.monthlySummary);
    } finally {
      setIsUpdating(null);
    }
  };

  const handleSaveIntegrationUrl = async (integrationKey: 'slack_webhook' | 'teams_webhook' | 'custom_webhook', url: string) => {
    if (!user || isSavingIntegration) return;

    // Basic URL validation (optional, can be more sophisticated)
    if (url && !url.startsWith('https://')) {
      toast.error('Please enter a valid HTTPS webhook URL.');
      return;
    }

    setIsSavingIntegration(integrationKey);
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        [`notifications.${integrationKey}`]: url,
      });
      toast.success(`${integrationKey.split('_')[0]} webhook URL ${url ? 'saved' : 'cleared'} successfully.`);
      // No optimistic update needed here as the input field directly reflects the state being saved.
    } catch (err: unknown) {
      console.error(`Failed to save ${integrationKey} URL:`, err);
      toast.error(`Failed to save ${integrationKey.split('_')[0]} webhook URL: Please try again.`);
    } finally {
      setIsSavingIntegration(null);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const isEnterprisePlan = currentPlan.id === 'enterprise';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Notification Preferences Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Notification Preferences</h3>
        <p className="mt-1 text-sm text-gray-500">Customize how you receive notifications about your domains.</p>
        <div className="mt-6 space-y-6">
          <div className="relative flex items-start">
            <div className="flex items-center h-5">
              <input
                id="email-notifications"
                name="email-notifications"
                type="checkbox"
                disabled={!!isUpdating}
                checked={emailNotifications}
                onChange={(e) => handleToggleSetting('email', e.target.checked)}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="email-notifications" className="font-medium text-gray-700">
                Email Notifications
              </label>
              <p className="text-gray-500">Receive email alerts when your certificates are about to expire.</p>
            </div>
          </div>
          <div className="relative flex items-start">
            <div className="flex items-center h-5">
              <input
                id="sms-notifications"
                name="sms-notifications"
                type="checkbox"
                disabled={!!isUpdating}
                checked={smsNotifications}
                onChange={(e) => handleToggleSetting('sms', e.target.checked)}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="sms-notifications" className="font-medium text-gray-700">
                SMS Notifications
              </label>
              <p className="text-gray-500">Receive text message alerts for critical expirations. (Standard and Enterprise plans only)</p>
            </div>
          </div>
          <div className="relative flex items-start">
            <div className="flex items-center h-5">
              <input
                id="weekly-summary"
                name="weekly-summary"
                type="checkbox"
                disabled={!!isUpdating}
                checked={weeklySummary}
                onChange={(e) => handleToggleSetting('weeklySummary', e.target.checked)}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="weekly-summary" className="font-medium text-gray-700">
                Weekly Summary
              </label>
              <p className="text-gray-500">Receive a weekly email summary of your domain certificate status.</p>
            </div>
          </div>
          <div className="relative flex items-start">
            <div className="flex items-center h-5">
              <input
                id="monthly-summary"
                name="monthly-summary"
                type="checkbox"
                disabled={!!isUpdating}
                checked={monthlySummary}
                onChange={(e) => handleToggleSetting('monthlySummary', e.target.checked)}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="monthly-summary" className="font-medium text-gray-700">
                Monthly Summary
              </label>
              <p className="text-gray-500">Receive a monthly email summary of your domain certificate status.</p>
            </div>
          </div>
        </div>
      </div>
      {/* Integrations Section */}
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Integrations</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Slack Integration Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-2">
              <FaSlack className="text-3xl mr-2 text-[#4A154B]" />
              <span className="text-xl font-bold text-gray-900">Slack</span>
            </div>
            <p className="text-gray-600 mb-1">Receive notifications directly in your Slack channel.</p>
            {!isEnterprisePlan && <p className="text-xs text-gray-500 mb-4">(Requires Enterprise plan)</p>}
            {isEnterprisePlan && <p className="text-xs text-gray-500 mb-4 invisible">(Placeholder for spacing)</p>}
            <div className="relative flex items-center mt-2">
              <input
                id="slack-webhook-url"
                name="slack-webhook-url"
                type="url"
                placeholder="Slack Webhook URL"
                value={slackWebhookUrl}
                onChange={(e) => setSlackWebhookUrl(e.target.value)}
                disabled={isSavingIntegration === 'slack_webhook' || !isEnterprisePlan}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
              {isEnterprisePlan && (
                <button
                  onClick={async () => {
                    const auth = getAuth();
                    const user = auth.currentUser;
                    if (!user) {
                      throw new Error("User not authenticated");
                    }

                    const token = await getIdToken(user);
                    try {
                      const response = await fetch("https://remindme-planet.appspot.com/slack_dry_run", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "Authorization": `Bearer ${token}`,
                        },
                        body: JSON.stringify({ webhook_url: slackWebhookUrl }),
                      });
                      if (response.ok) {
                        toast.success("Slack webhook test successful!");
                      } else {
                        toast.error("Slack webhook test failed.");
                      }
                    } catch {
                      toast.error("Error testing Slack webhook.");
                    }
                  }}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Test
                </button>
              )}
            </div>
          </div>
          {isEnterprisePlan ? (
            <button
              onClick={() => handleSaveIntegrationUrl('slack_webhook', slackWebhookUrl)}
              disabled={isSavingIntegration === 'slack_webhook'}
              className={`mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full ${isSavingIntegration === 'slack_webhook' ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#4A154B] hover:bg-[#3e1240]'
                }`}
            >
              <FaSlack className="mr-2 text-lg" />
              {isSavingIntegration === 'slack_webhook' ? 'Saving...' : (slackWebhookUrl ? 'Update Webhook' : 'Save Webhook')}
            </button>
          ) : (
            <button
              onClick={() => navigate('/billing')}
              className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full bg-primary-500 hover:bg-primary-600"
            >
              <FiZap className="mr-2 text-lg" />
              Upgrade to Enable
            </button>
          )}
        </div>

        {/* Teams Integration Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-2">
              <FiUsers className="text-3xl mr-2 text-[#6264A7]" />
              <span className="text-xl font-bold text-gray-900">Microsoft Teams</span>
            </div>
            <p className="text-gray-600 mb-1">Get notifications in your Microsoft Teams channel.</p>
            {!isEnterprisePlan && <p className="text-xs text-gray-500 mb-4">(Requires Enterprise plan)</p>}
            {isEnterprisePlan && <p className="text-xs text-gray-500 mb-4 invisible">(Placeholder for spacing)</p>}
            <div className="relative flex items-center mt-2">
              <input
                id="teams-webhook-url"
                name="teams-webhook-url"
                type="url"
                placeholder="Teams Webhook URL"
                value={teamsWebhookUrl}
                onChange={(e) => setTeamsWebhookUrl(e.target.value)}
                disabled={isSavingIntegration === 'teams_webhook' || !isEnterprisePlan}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
              {isEnterprisePlan && (
                <button
                  onClick={async () => {
                    const auth = getAuth();
                    const user = auth.currentUser;
                    if (!user) {
                      throw new Error("User not authenticated");
                    }

                    const token = await getIdToken(user);
                    try {
                      const response = await fetch("https://remindme-planet.uc.r.appspot.com/teams_dry_run", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "Authorization": `Bearer ${token}`,
                        },
                        body: JSON.stringify({ webhook_url: teamsWebhookUrl }),
                      });
                      if (response.ok) {
                        toast.success("Teams webhook test successful!");
                      } else {
                        toast.error("Teams webhook test failed.");
                      }
                    } catch {
                      toast.error("Error testing Teams webhook.");
                    }
                  }}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Test
                </button>
              )}
            </div>
          </div>
          {isEnterprisePlan ? (
            <button
              onClick={() => handleSaveIntegrationUrl('teams_webhook', teamsWebhookUrl)}
              disabled={isSavingIntegration === 'teams_webhook'}
              className={`mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full ${isSavingIntegration === 'teams_webhook' ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#6264A7] hover:bg-[#4a4c8c]'
                }`}
            >
              <FiUsers className="mr-2 text-lg" />
              {isSavingIntegration === 'teams_webhook' ? 'Saving...' : (teamsWebhookUrl ? 'Update Webhook' : 'Save Webhook')}
            </button>
          ) : (
            <button
              onClick={() => navigate('/billing')}
              className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full bg-primary-500 hover:bg-primary-600"
            >
              <FiZap className="mr-2 text-lg" />
              Upgrade to Enable
            </button>
          )}
        </div>

        {/* Webhooks Integration Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-2">
              <FaRocketchat className="text-3xl mr-2 text-pink-600" />
              <span className="text-xl font-bold text-gray-900">Custom Webhook</span>
            </div>
            <p className="text-gray-600 mb-1">Send notifications to any custom webhook endpoint.</p>
            {!isEnterprisePlan && <p className="text-xs text-gray-500 mb-4">(Requires Enterprise plan)</p>}
            {isEnterprisePlan && <p className="text-xs text-gray-500 mb-4 invisible">(Placeholder for spacing)</p>}
            <input
              type="url"
              placeholder="Custom Webhook URL"
              value={customWebhookUrl}
              onChange={(e) => setCustomWebhookUrl(e.target.value)}
              disabled={isSavingIntegration === 'custom_webhook' || !isEnterprisePlan}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm mb-2"
            />
          </div>
          {isEnterprisePlan ? (
            <button
              onClick={() => handleSaveIntegrationUrl('custom_webhook', customWebhookUrl)}
              disabled={isSavingIntegration === 'custom_webhook'}
              className={`mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full ${isSavingIntegration === 'custom_webhook' ? 'bg-gray-400 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-700'
                }`}
            >
              <FaRocketchat className="mr-2 text-lg" />
              {isSavingIntegration === 'custom_webhook' ? 'Saving...' : (customWebhookUrl ? 'Update Webhook' : 'Save Webhook')}
            </button>
          ) : (
            <button
              onClick={() => navigate('/billing')}
              className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-white font-medium text-base transition w-full bg-primary-500 hover:bg-primary-600"
            >
              <FiZap className="mr-2 text-lg" />
              Upgrade to Enable
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
